import { useState, useEffect, useRef, Fragment } from "react";
import {
    Container,
    Box,
    Paper,
    Button,
    TextField,
    Grid,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@mui/material";

import ChronoSection from "../components/ChronoSection";
import ChronoTitle from "../components/ChronoTitle";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

const STORAGE_KEY = "tramesDuJour";
const PENDING_KEY = "tramesEnAttente";
const RETRY_INTERVAL_MS = 5000;

// ============================================================================
// FORMAT TN OFFICIEL TAG HEUER
// ============================================================================
function buildTagHeuerFrame(seq, channel, candidate, dateUTC) {
    const epoch2000 = Date.UTC(2000, 0, 1);
    const dayUTC = Date.UTC(
        dateUTC.getUTCFullYear(),
        dateUTC.getUTCMonth(),
        dateUTC.getUTCDate()
    );
    const days = Math.round((dayUTC - epoch2000) / 86400000);
    const dayStr = String(days).padStart(5, "0");

    const hh = String(dateUTC.getUTCHours()).padStart(2, "0");
    const mm = String(dateUTC.getUTCMinutes()).padStart(2, "0");
    const ss = String(dateUTC.getUTCSeconds()).padStart(2, "0");
    const frac = String(Math.floor(dateUTC.getUTCMilliseconds() * 1000)).padStart(5, "0");

    const timeStr = `${hh}:${mm}:${ss}.${frac}`;

    let channelLabel;
    if (/^M\d+$/.test(channel)) {
        channelLabel = channel.replace("M", "").padStart(2, "0");
    } else {
        channelLabel = String(channel).padStart(2, "0");
    }

    const candidateStr = String(candidate ?? 0).padStart(4, "0");
    const seqStr = String(seq).padStart(4, "0");

    return `TN ${candidateStr} ${seqStr} ${channelLabel} ${timeStr} ${dayStr}`;
}

// ============================================================================
// FORMAT UTC + OFFSET
// ============================================================================
function formatAdjustedUTC(date, offset) {
    const utc = date.getTime() + offset * 3600 * 1000;
    const d = new Date(utc);

    const hh = String(d.getUTCHours()).padStart(2, "0");
    const mm = String(d.getUTCMinutes()).padStart(2, "0");
    const ss = String(d.getUTCSeconds()).padStart(2, "0");

    return `${hh}:${mm}:${ss}`;
}

// ============================================================================
// STOCKAGE LOCAL — chargement au démarrage
// ============================================================================
function loadStoredLogs() {
    try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        // `date` a été sérialisé en chaîne par JSON.stringify — on le
        // reconvertit en vrai objet Date, sinon buildTagHeuerFrame plante
        // (appelle .getUTCFullYear() etc. sur une chaîne) si on modifie un
        // dossard après un rechargement de page.
        return saved.map((l) => ({ ...l, date: new Date(l.date) }));
    } catch {
        return [];
    }
}

function loadStoredPending() {
    try {
        return JSON.parse(localStorage.getItem(PENDING_KEY) || "[]");
    } catch {
        return [];
    }
}

// ============================================================================
// ENVOI SERVEUR (sans monitoring — le retour est géré par l'appelant)
// ============================================================================
async function sendEvent(event) {
    try {
        const res = await fetch(SERVER_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Api-Key": API_KEY,
            },
            body: JSON.stringify(event),
        });
        return res.ok;
    } catch {
        return false;
    }
}

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
export default function Timer() {
    const [configOpen, setConfigOpen] = useState(true);

    const [chronoId, setChronoId] = useState("chrono-A1");
    const [userName, setUserName] = useState("Christoph");
    const [channel, setChannel] = useState("M1");
    const [gmtOffset, setGmtOffset] = useState(0);

    const [candidate, setCandidate] = useState("");
    const [candidateLocked, setCandidateLocked] = useState(false);
    const candidateRef = useRef(null);

    // Trames restaurées depuis localStorage au démarrage — survit à un
    // rechargement accidentel de la page (tablette qui redémarre, onglet
    // fermé par erreur...).
    const [logs, setLogs] = useState(loadStoredLogs);

    // La séquence reprend après la dernière trame connue localement,
    // plutôt que de redémarrer à 1 — sans ça, un rechargement de page en
    // pleine course écraserait silencieusement les trames déjà envoyées
    // (même chrono_id + canal + séquence = mise à jour côté serveur, pas
    // ajout).
    const [sequence, setSequence] = useState(() => {
        const stored = loadStoredLogs();
        const maxSeq = stored.reduce((max, l) => Math.max(max, l.seq || 0), 0);
        return maxSeq + 1;
    });

    // File d'attente des trames non confirmées par le serveur — retentées
    // automatiquement, persistée en localStorage pour survivre à une
    // fermeture/rechargement pendant une coupure réseau.
    const [pendingEvents, setPendingEvents] = useState(loadStoredPending);

    const [editingCandidateId, setEditingCandidateId] = useState(null);
    const [editingValue, setEditingValue] = useState("");

    // Première ligne auto-activée
    const [autoEditRowId, setAutoEditRowId] = useState(null);
    const autoEditRef = useRef(null);

    // Monitoring
    const [sentCount, setSentCount] = useState(0);
    const [okCount, setOkCount] = useState(0);
    const [errorCount, setErrorCount] = useState(0);
    const [serverOnline, setServerOnline] = useState(true);

    // Protection contre la fermeture accidentelle de l'onglet (activable
    // manuellement). ⚠️ On ne peut PAS empêcher réellement la fermeture —
    // aucun navigateur ne le permet, par sécurité. On peut seulement
    // déclencher la boîte de dialogue native de confirmation ("Quitter le
    // site ?"). Le texte affiché est imposé par le navigateur (impossible
    // à personnaliser sur Chrome/Firefox/Edge récents) et l'utilisateur
    // peut toujours choisir de quitter quand même.
    const [protectClose, setProtectClose] = useState(false);

    useEffect(() => {
        if (!protectClose) return;

        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = ""; // requis par certains navigateurs pour afficher la boîte de dialogue
            return "";
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [protectClose]);

    // Focus automatique sur le champ candidat
    useEffect(() => {
        if (!candidateLocked && !configOpen && candidateRef.current) {
            candidateRef.current.focus();
        }
    }, [candidateLocked, configOpen]);

    // Focus automatique sur la première ligne
    useEffect(() => {
        if (autoEditRowId && autoEditRef.current) {
            autoEditRef.current.focus();
        }
    }, [autoEditRowId]);

    // Sauvegarde automatique de la file d'attente à chaque changement.
    useEffect(() => {
        localStorage.setItem(PENDING_KEY, JSON.stringify(pendingEvents));
    }, [pendingEvents]);

    // Retente l'envoi des trames en attente toutes les 5s — même principe
    // que ConnectChrono.jsx.
    useEffect(() => {
        const interval = setInterval(async () => {
            if (pendingEvents.length === 0) return;

            const stillPending = [];
            let anySuccess = false;

            for (const ev of pendingEvents) {
                const ok = await sendEvent(ev);
                if (ok) {
                    anySuccess = true;
                } else {
                    stillPending.push(ev);
                }
            }

            if (anySuccess) {
                setOkCount((c) => c + (pendingEvents.length - stillPending.length));
            }
            setPendingEvents(stillPending);
            setServerOnline(stillPending.length === 0);
        }, RETRY_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [pendingEvents]);

    // ========================================================================
    // AJOUT TRAME + STOCKAGE LOCAL
    // ========================================================================
    function addLog(entry) {
        const id = entry.id || `${Date.now()}-${Math.random()}`;
        const finalEntry = { ...entry, id };

        setLogs((prev) => {
            const updated = [finalEntry, ...prev.slice(0, 199)];
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
            return updated;
        });

        return id;
    }

    // Envoie immédiatement ; si ça échoue, met en file d'attente pour
    // retenter automatiquement (au lieu de simplement afficher une erreur
    // et perdre la trame).
    async function dispatchEvent(event) {
        setSentCount((c) => c + 1);

        const ok = await sendEvent(event);
        if (ok) {
            setOkCount((c) => c + 1);
            setServerOnline(true);
        } else {
            setErrorCount((c) => c + 1);
            setServerOnline(false);
            setPendingEvents((prev) => [...prev, event]);
        }
    }

    // ========================================================================
    // IMPULSION
    // ========================================================================
    async function sendImpulse() {
        const now = new Date();
        const adjustedUTC = new Date(now.getTime() + gmtOffset * 3600 * 1000);

        const frame = buildTagHeuerFrame(
            sequence,
            channel,
            candidate || null,
            adjustedUTC
        );

        const event = {
            chrono_id: chronoId,
            channel,
            candidate: candidate || null,
            sequence,
            source_format: "tagheuer",
            user: userName,
            raw_value: frame,
            timestamp: adjustedUTC.toISOString(),
            local_timestamp: now.toISOString(),
        };

        const newRowId = addLog({
            id: `${Date.now()}-${Math.random()}`,
            time: adjustedUTC.toISOString(),
            channel,
            candidate: candidate || "",
            raw: frame,
            seq: sequence,
            date: adjustedUTC,
        });

        setSequence((s) => s + 1);

        await dispatchEvent(event);

        // Gestion dossard manquant → activer première ligne
        if (!candidate) {
            setAutoEditRowId(newRowId);
            setCandidateLocked(true);

            setTimeout(() => {
                setAutoEditRowId(null);
                setCandidateLocked(false);
            }, 5000); // 5 secondes
        }

        if (navigator.vibrate) navigator.vibrate(50);
        setCandidate("");
    }

    // ========================================================================
    // MODIFICATION DOSSARD
    // ========================================================================
    async function updateCandidate(id, newCandidate) {
        let updatedRow = null;

        setLogs((prev) => {
            const updated = prev.map((l) => {
                if (l.id !== id) return l;

                // 🔁 recalcul TN complet avec le nouveau dossard
                const correctedRaw = buildTagHeuerFrame(
                    l.seq,
                    l.channel,
                    newCandidate,
                    l.date
                );

                const newRow = {
                    ...l,
                    candidate: newCandidate,
                    raw: correctedRaw,   // 🔥 mise à jour du RAW
                };
                updatedRow = newRow;
                return newRow;
            });

            // 🔁 mise à jour du localStorage avec les trames corrigées
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

            return updated;
        });

        if (!updatedRow) return;

        const event = {
            chrono_id: chronoId,
            channel: updatedRow.channel,
            candidate: newCandidate || null,
            sequence: updatedRow.seq,
            source_format: "tagheuer",
            user: userName,
            raw_value: updatedRow.raw,
            timestamp: updatedRow.date.toISOString(),
            local_timestamp: new Date().toISOString(),
        };

        await dispatchEvent(event);
    }


    // ========================================================================
    // HEURE SYNCHRONISÉE
    // ========================================================================
    const [currentTime, setCurrentTime] = useState(new Date());
    useEffect(() => {
        const interval = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    const displayTime = formatAdjustedUTC(currentTime, gmtOffset);

    // ========================================================================
    // EXPORT CSV
    // ========================================================================
    function exportCSV() {
        const rows = logs; // 🔁 on prend l'état actuel en mémoire

        const header = "seq,candidate,channel,time,raw\n";
        const body = rows
            .map(r =>
                `${r.seq},${r.candidate || ""},${r.channel},${r.time},${r.raw}`
            )
            .join("\n");

        const csv = header + body;

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "trames_du_jour.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    // Efface les trames stockées localement (nouvelle journée/course) —
    // ne touche pas au serveur, juste ce navigateur.
    function clearStoredLogs() {
        if (!window.confirm("Effacer les trames stockées localement sur cet appareil ? (le serveur n'est pas affecté)")) {
            return;
        }
        setLogs([]);
        setSequence(1);
        localStorage.removeItem(STORAGE_KEY);
    }


    // ========================================================================
    // RENDER
    // ============================================================================
    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>

            {/* Bouton modifier configuration à gauche + protection fermeture */}
            <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
                <Box>
                    {!configOpen && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => setConfigOpen(true)}
                        >
                            Modifier configuration
                        </Button>
                    )}
                </Box>

                <Button
                    variant={protectClose ? "contained" : "outlined"}
                    color={protectClose ? "warning" : "inherit"}
                    size="small"
                    onClick={() => setProtectClose((v) => !v)}
                >
                    {protectClose
                        ? "🔒 Protection fermeture activée"
                        : "🔓 Activer la protection contre la fermeture"}
                </Button>
            </Box>

            {/* Monitoring serveur */}
            <Box
                sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: "12px",
                    background: serverOnline ? "#0a0" : "#a00",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    textAlign: "center",
                    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                }}
            >
                {serverOnline ? "Serveur OK" : "Erreur serveur"}
                <Box sx={{ mt: 1, fontSize: "0.95rem" }}>
                    Trames envoyées : {sentCount} — OK : {okCount} — Erreurs : {errorCount}
                    {pendingEvents.length > 0 && (
                        <> — ⏳ En attente de renvoi : {pendingEvents.length}</>
                    )}
                </Box>
            </Box>

            {/* CONFIGURATION */}
            {configOpen && (
                <ChronoSection>
                    <ChronoTitle>Configuration du chrono</ChronoTitle>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Nom du chrono"
                                fullWidth
                                value={chronoId}
                                onChange={(e) => setChronoId(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Utilisateur"
                                fullWidth
                                value={userName}
                                onChange={(e) => setUserName(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Canal (ex: M1, M3, M8)"
                                fullWidth
                                value={channel}
                                onChange={(e) => setChannel(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Décalage horaire (GMT)"
                                fullWidth
                                select
                                SelectProps={{ native: true }}
                                value={gmtOffset}
                                onChange={(e) => setGmtOffset(Number(e.target.value))}
                            >
                                <option value={-3}>GMT-3</option>
                                <option value={-2}>GMT-2</option>
                                <option value={-1}>GMT-1</option>
                                <option value={0}>GMT</option>
                                <option value={1}>GMT+1</option>
                                <option value={2}>GMT+2</option>
                                <option value={3}>GMT+3</option>
                            </TextField>
                        </Grid>
                    </Grid>

                    {logs.length > 0 && (
                        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                            <Box sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
                                {logs.length} trame(s) déjà en mémoire sur cet appareil (séquence reprendra à{" "}
                                {sequence}).
                            </Box>
                            <Button size="small" color="error" onClick={clearStoredLogs}>
                                Effacer les trames locales
                            </Button>
                        </Box>
                    )}

                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{ mt: 3 }}
                        onClick={() => setConfigOpen(false)}
                    >
                        Commencer
                    </Button>
                </ChronoSection>
            )}

            {/* MODE CAPTURE */}
            {!configOpen && (
                <>
                    <ChronoSection>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 4,
                            }}
                        >
                            {/* Heure synchronisée */}
                            <Box sx={{ textAlign: "center" }}>
                                <Box
                                    sx={{
                                        background: "#111",
                                        color: "#0f0",
                                        px: 3,
                                        py: 1.5,
                                        borderRadius: "12px",
                                        fontFamily: "monospace",
                                        fontSize: "2rem",
                                        fontWeight: "bold",
                                        mb: 2,
                                        boxShadow: "0 0 12px rgba(0,255,0,0.4)",
                                    }}
                                >
                                    {displayTime}{" "}
                                    <span style={{ fontSize: "1.2rem" }}>
                                        (GMT{gmtOffset >= 0 ? `+${gmtOffset}` : gmtOffset})
                                    </span>
                                </Box>

                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={sendImpulse}
                                    sx={{
                                        width: 180,
                                        height: 180,
                                        borderRadius: "50%",
                                        fontSize: "1.3rem",
                                        fontWeight: 700,
                                        boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
                                    }}
                                >
                                    IMPULSION
                                </Button>
                            </Box>

                            {/* Champ dossard */}
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    inputRef={candidateRef}
                                    disabled={candidateLocked}
                                    label="Candidat (dossard, optionnel)"
                                    fullWidth
                                    value={candidate}
                                    onChange={(e) => setCandidate(e.target.value)}
                                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                />
                            </Box>
                        </Box>
                    </ChronoSection>

                    {/* Tableau des impulsions */}
                    <ChronoSection>
                        <ChronoTitle>Impulsions envoyées</ChronoTitle>

                        <Paper
                            variant="outlined"
                            sx={{
                                borderRadius: "16px",
                                overflowX: "auto",
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Seq</TableCell>
                                        <TableCell>Candidat</TableCell>
                                        <TableCell>Heure (UTC+offset)</TableCell>
                                        <TableCell>Canal</TableCell>
                                        <TableCell>Brut</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {logs.map((row) => (
                                        <Fragment key={row.id}>
                                            <TableRow>
                                                <TableCell>{row.seq}</TableCell>

                                                <TableCell>
                                                    {editingCandidateId === row.id ||
                                                    autoEditRowId === row.id ? (
                                                        <TextField
                                                            inputRef={
                                                                autoEditRowId === row.id
                                                                    ? autoEditRef
                                                                    : null
                                                            }
                                                            autoFocus={
                                                                autoEditRowId === row.id
                                                            }
                                                            fullWidth
                                                            size="large"
                                                            value={editingValue}
                                                            onChange={(e) =>
                                                                setEditingValue(e.target.value)
                                                            }
                                                            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                                            sx={{
                                                                fontSize: "1.4rem",
                                                                py: 2,
                                                            }}
                                                        />
                                                    ) : (
                                                        row.candidate || (
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => {
                                                                    setEditingCandidateId(row.id);
                                                                    setEditingValue("");
                                                                }}
                                                            >
                                                                Ajouter dossard
                                                            </Button>
                                                        )
                                                    )}
                                                </TableCell>

                                                <TableCell>
                                                    {formatAdjustedUTC(
                                                        new Date(row.time),
                                                        0
                                                    )}
                                                </TableCell>

                                                <TableCell>{row.channel}</TableCell>
                                                <TableCell>{row.raw}</TableCell>
                                            </TableRow>

                                            {(editingCandidateId === row.id || autoEditRowId === row.id) && (
                                                <TableRow>
                                                    <TableCell colSpan={5}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "flex-start",
                                                                gap: 1.5,
                                                                mt: 1,
                                                                mb: 1,
                                                            }}
                                                        >
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                sx={{
                                                                    minWidth: "90px",
                                                                    py: 1,
                                                                    fontSize: "1rem",
                                                                    borderRadius: "12px",
                                                                }}
                                                                onClick={() => {
                                                                    setEditingCandidateId(null);
                                                                    setAutoEditRowId(null);
                                                                    setEditingValue("");
                                                                }}
                                                            >
                                                                Effacer
                                                            </Button>

                                                            <Button
                                                                variant="contained"
                                                                color="success"
                                                                sx={{
                                                                    minWidth: "90px",
                                                                    py: 1,
                                                                    fontSize: "1rem",
                                                                    borderRadius: "12px",
                                                                }}
                                                                onClick={() => {
                                                                    updateCandidate(
                                                                        editingCandidateId || autoEditRowId,
                                                                        editingValue
                                                                    );
                                                                    setEditingCandidateId(null);
                                                                    setAutoEditRowId(null);
                                                                    setEditingValue("");
                                                                }}
                                                            >
                                                                Valider
                                                            </Button>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                        </Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>

                        {/* Export CSV sous le tableau */}
                        <Button
                            variant="outlined"
                            color="primary"
                            sx={{ mt: 2 }}
                            onClick={exportCSV}
                        >
                            Exporter les trames du jour (CSV)
                        </Button>

                    </ChronoSection>
                </>
            )}
        </Container>
    );
}